import apiService from '../../../services/apiService';

const BASE_URL = '/ai-crm/admin';

// ==================== Types ====================

export interface AdminCompany {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userCount: number;
  subscription?: {
    id: string;
    plan: string;
    planName?: string;
    status: string;
    amount: number;
    billingCycle: string;
    currentPeriodEnd: string;
  } | null;
}

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  isActive: boolean;
  companyId?: string;
  companyName?: string;
  companyCode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscription {
  id: string;
  subscriptionNo: string;
  companyId: string;
  companyName: string;
  companyCode: string;
  plan: string;
  planDisplayName?: string;
  status: string;
  billingCycle: string;
  amount: number;
  currency: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  autoRenew: boolean;
  maxUsers?: number;
  maxLeads?: number;
  maxAIQueries?: number;
  maxEmailCampaigns?: number;
  currentAIQueries: number;
  currentEmailsSent: number;
  createdAt: string;
}

export interface AdminInvoice {
  id: string;
  invoiceNo: string;
  companyId: string;
  companyName: string;
  companyCode: string;
  plan?: string;
  amount: number;
  tax: number;
  total: number;
  currency: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  paidAt?: string;
  createdAt: string;
}

export interface AdminPlan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  features: string[];
  priceMonthly: number;
  priceYearly: number;
  maxUsers?: number;
  maxLeads?: number;
  maxAIQueries?: number;
  maxEmailCampaigns?: number;
  maxStorage?: number;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
  isActive: boolean;
  isRecommended: boolean;
  sortOrder: number;
  activeSubscriptionCount?: number;
}

export interface AuditLog {
  id: string;
  adminUserId: string;
  adminEmail: string;
  adminName?: string;
  action: string;
  targetType: string;
  targetId: string;
  details?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface OverviewStats {
  companies: { total: number; active: number };
  users: { total: number; active: number };
  subscriptions: { active: number; pastDue: number; expiringSoon: number };
  invoices: { pending: number };
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ==================== API Functions ====================

// Dashboard
export const getOverview = async (): Promise<{ success: boolean; data: OverviewStats }> => {
  return apiService.get(`${BASE_URL}/overview`);
};

export const getRecentActivity = async (limit = 10): Promise<{ success: boolean; data: AuditLog[] }> => {
  return apiService.get(`${BASE_URL}/recent-activity`, { params: { limit } });
};

// Companies
export const getCompanies = async (params: PaginationParams & { isActive?: boolean }): Promise<PaginatedResponse<AdminCompany>> => {
  return apiService.get(`${BASE_URL}/companies`, { params });
};

export const getCompanyById = async (id: string): Promise<{ success: boolean; data: AdminCompany }> => {
  return apiService.get(`${BASE_URL}/companies/${id}`);
};

export const createCompany = async (data: { name: string; code: string; email?: string; phone?: string; address?: string; planId?: string; billingCycle?: 'MONTHLY' | 'YEARLY' }): Promise<{ success: boolean; data: AdminCompany }> => {
  return apiService.post(`${BASE_URL}/companies`, data);
};

export const updateCompany = async (id: string, data: Partial<AdminCompany>): Promise<{ success: boolean; data: AdminCompany }> => {
  return apiService.put(`${BASE_URL}/companies/${id}`, data);
};

export const toggleCompanyActive = async (id: string, isActive: boolean): Promise<{ success: boolean; data: AdminCompany }> => {
  return apiService.put(`${BASE_URL}/companies/${id}/activate`, { isActive });
};

export const getCompanyStats = async (id: string): Promise<{ success: boolean; data: any }> => {
  return apiService.get(`${BASE_URL}/companies/${id}/stats`);
};

export const deleteCompany = async (id: string): Promise<{ success: boolean; message: string }> => {
  return apiService.delete(`${BASE_URL}/companies/${id}`);
};

// Users
export const getUsers = async (params: PaginationParams & { companyId?: string; role?: string; isActive?: boolean }): Promise<PaginatedResponse<AdminUser>> => {
  return apiService.get(`${BASE_URL}/users`, { params });
};

export const getUserById = async (id: string): Promise<{ success: boolean; data: AdminUser }> => {
  return apiService.get(`${BASE_URL}/users/${id}`);
};

export const createUser = async (data: { email: string; name?: string; password: string; role?: string; companyId?: string }): Promise<{ success: boolean; data: AdminUser }> => {
  return apiService.post(`${BASE_URL}/users`, data);
};

export const updateUser = async (id: string, data: Partial<AdminUser>): Promise<{ success: boolean; data: AdminUser }> => {
  return apiService.put(`${BASE_URL}/users/${id}`, data);
};

export const resetUserPassword = async (id: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
  return apiService.post(`${BASE_URL}/users/${id}/reset-password`, { newPassword });
};

export const deleteUser = async (id: string): Promise<{ success: boolean; message: string }> => {
  return apiService.delete(`${BASE_URL}/users/${id}`);
};

// Plans
export const getPlans = async (includeInactive = false): Promise<{ success: boolean; data: AdminPlan[] }> => {
  return apiService.get(`${BASE_URL}/plans`, { params: { includeInactive } });
};

export const getPlanById = async (id: string): Promise<{ success: boolean; data: AdminPlan }> => {
  return apiService.get(`${BASE_URL}/plans/${id}`);
};

export const createPlan = async (data: Partial<AdminPlan>): Promise<{ success: boolean; data: AdminPlan }> => {
  return apiService.post(`${BASE_URL}/plans`, data);
};

export const updatePlan = async (id: string, data: Partial<AdminPlan>): Promise<{ success: boolean; data: AdminPlan }> => {
  return apiService.put(`${BASE_URL}/plans/${id}`, data);
};

export const deletePlan = async (id: string): Promise<{ success: boolean; message: string }> => {
  return apiService.delete(`${BASE_URL}/plans/${id}`);
};

export const togglePlanActive = async (id: string, isActive: boolean): Promise<{ success: boolean; data: AdminPlan }> => {
  return apiService.put(`${BASE_URL}/plans/${id}/toggle`, { isActive });
};

// Subscriptions
export const getSubscriptions = async (params: PaginationParams & { status?: string; planId?: string; billingCycle?: string }): Promise<PaginatedResponse<AdminSubscription>> => {
  return apiService.get(`${BASE_URL}/subscriptions`, { params });
};

export const getSubscriptionById = async (id: string): Promise<{ success: boolean; data: AdminSubscription }> => {
  return apiService.get(`${BASE_URL}/subscriptions/${id}`);
};

export const overrideSubscription = async (companyId: string, data: {
  planId?: string;
  maxUsers?: number;
  maxLeads?: number;
  maxAIQueries?: number;
  maxEmailCampaigns?: number;
  amount?: number;
  billingCycle?: string;
  currentPeriodEnd?: string;
  status?: string;
}): Promise<{ success: boolean; data: AdminSubscription }> => {
  return apiService.put(`${BASE_URL}/subscriptions/${companyId}/override`, data);
};

export const extendSubscription = async (id: string, days: number): Promise<{ success: boolean; data: AdminSubscription }> => {
  return apiService.post(`${BASE_URL}/subscriptions/${id}/extend`, { days });
};

export const suspendSubscription = async (id: string, reason?: string): Promise<{ success: boolean; data: AdminSubscription }> => {
  return apiService.post(`${BASE_URL}/subscriptions/${id}/suspend`, { reason });
};

export const reactivateSubscription = async (id: string): Promise<{ success: boolean; data: AdminSubscription }> => {
  return apiService.post(`${BASE_URL}/subscriptions/${id}/reactivate`, {});
};

// Invoices
export const getInvoices = async (params: PaginationParams & { status?: string; companyId?: string; startDate?: string; endDate?: string }): Promise<PaginatedResponse<AdminInvoice>> => {
  return apiService.get(`${BASE_URL}/invoices`, { params });
};

export const markInvoicePaid = async (id: string, paymentMethod = 'MANUAL'): Promise<{ success: boolean; data: AdminInvoice }> => {
  return apiService.post(`${BASE_URL}/invoices/${id}/mark-paid`, { paymentMethod });
};

export const refundInvoice = async (id: string, amount?: number, reason?: string): Promise<{ success: boolean; data: AdminInvoice }> => {
  return apiService.post(`${BASE_URL}/invoices/${id}/refund`, { amount, reason });
};

// Audit Logs
export const getAuditLogs = async (params: PaginationParams & { adminUserId?: string; action?: string; targetType?: string; startDate?: string; endDate?: string }): Promise<PaginatedResponse<AuditLog>> => {
  return apiService.get(`${BASE_URL}/audit-logs`, { params });
};

export const getAuditLogById = async (id: string): Promise<{ success: boolean; data: AuditLog & { targetDetails?: any } }> => {
  return apiService.get(`${BASE_URL}/audit-logs/${id}`);
};

export const getActionTypes = async (): Promise<{ success: boolean; data: string[] }> => {
  return apiService.get(`${BASE_URL}/audit-logs/action-types`);
};

// User Activities
export interface UserActivity {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  companyId: string;
  companyName: string;
  action: string;
  targetType?: string;
  targetId?: string;
  targetName?: string;
  details?: Record<string, any>;
  ipAddress?: string;
  createdAt: string;
}

export const getUserActivities = async (params: PaginationParams & {
  userId?: string;
  companyId?: string;
  action?: string;
  targetType?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<PaginatedResponse<UserActivity>> => {
  return apiService.get(`${BASE_URL}/user-activities`, { params });
};

export const getUserActivityActionTypes = async (): Promise<{ success: boolean; data: string[] }> => {
  return apiService.get(`${BASE_URL}/user-activities/action-types`);
};

export const getUserActivityStats = async (): Promise<{ success: boolean; data: any }> => {
  return apiService.get(`${BASE_URL}/user-activities/stats`);
};

export default {
  // Dashboard
  getOverview,
  getRecentActivity,
  // Companies
  getCompanies,
  getCompanyById,
  createCompany,
  updateCompany,
  toggleCompanyActive,
  getCompanyStats,
  deleteCompany,
  // Users
  getUsers,
  getUserById,
  createUser,
  updateUser,
  resetUserPassword,
  deleteUser,
  // Plans
  getPlans,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  togglePlanActive,
  // Subscriptions
  getSubscriptions,
  getSubscriptionById,
  overrideSubscription,
  extendSubscription,
  suspendSubscription,
  reactivateSubscription,
  // Invoices
  getInvoices,
  markInvoicePaid,
  refundInvoice,
  // Audit Logs
  getAuditLogs,
  getAuditLogById,
  getActionTypes,
  // User Activities
  getUserActivities,
  getUserActivityActionTypes,
  getUserActivityStats,
};
